/**
 * Trusted workload-issuer registry wire shapes. PureID is a relying party
 * on the customer's workload identity: a tenant admin registers the OIDC issuers it
 * trusts to attest its agents, and the mint verifies agent workload tokens against
 * them. Disabling an issuer REVOKES trust without deleting the record — every
 * subsequent agent mint from it fails closed.
 *
 *   GET  /api/v1/tenants/{tenant_id}/agents/workload-issuers          list
 *   POST /api/v1/tenants/{tenant_id}/agents/workload-issuers          register
 *   POST /api/v1/tenants/{tenant_id}/agents/workload-issuers/status   enable / disable
 *   POST /api/v1/tenants/{tenant_id}/agents/workload-issuers/category classify for the census
 */

import { z } from "zod";

import { CENSUS_CATEGORY_META } from "@/features/nhi-census/census-vocabulary";

export const issuerStatusSchema = z.enum(["active", "disabled"]);
export type IssuerStatus = z.infer<typeof issuerStatusSchema>;

// Assurance class — the trust band the issuer's tokens may reach (mirrors the
// backend AssuranceClass set). cloud_instance is the conservative default; the
// stronger bands gate higher-authority agent actions (P5).
export const assuranceClassSchema = z.enum([
  "cloud_instance",
  "hsm_custody",
  "vtpm_measured",
]);
export type AssuranceClass = z.infer<typeof assuranceClassSchema>;

/**
 * The census taxonomy for every workload identity federated from this issuer
 * (docs/product-areas.md).
 *
 * This is NOT a trust setting and it is not the principal kind. It cannot change
 * who is trusted, which audience is accepted, or which claim binds the subject —
 * it decides only how the workloads behind this issuer are COUNTED on the
 * non-human census. That is why it is the one field on this surface that can be
 * edited after registration, while the trust fields still cannot.
 *
 * Absent = unclassified, which the census renders as its own visible segment.
 */
export const issuerCategorySchema = z.enum([
  "cloud_workload",
  "pipeline",
  "service_account",
]);
export type IssuerCategory = z.infer<typeof issuerCategorySchema>;

/**
 * DERIVED from the census vocabulary, never restated.
 *
 * These three are exactly the issuer-assignable subset of the census's eight
 * categories (the other five are fixed by principal kind), so the label and
 * description an operator reads while classifying an issuer MUST be the ones the
 * census will then show them. Two hand-written copies had already drifted:
 * "pipeline" and "service_account" each carried a different description here
 * than on the roster, so the same segment was described two ways depending on
 * which page you were standing on — and the classification screen is where the
 * description does real work, because it is what the choice is made from.
 *
 * Typed as Record<IssuerCategory, …> so the census growing a category cannot
 * silently widen what an issuer may be set to; adding one here is still a
 * deliberate edit to issuerCategorySchema above.
 */
export const ISSUER_CATEGORY_META: Record<
  IssuerCategory,
  { label: string; description: string }
> = Object.fromEntries(
  issuerCategorySchema.options.map((c) => [
    c,
    {
      label: CENSUS_CATEGORY_META[c].label,
      description: CENSUS_CATEGORY_META[c].description,
    },
  ]),
) as Record<IssuerCategory, { label: string; description: string }>;

export const issuerTrustModeSchema = z.enum(["remote_jwks", "pinned_jwks"]);
export type IssuerTrustMode = z.infer<typeof issuerTrustModeSchema>;
const signingKeyMetadata = {
  kid: z.string().min(1).max(256),
  use: z.literal("sig").optional(),
  key_ops: z.tuple([z.literal("verify")]).optional(),
};
export const pinnedJWKSSchema = z.object({
  keys: z.array(z.discriminatedUnion("kty", [
    z.object({ ...signingKeyMetadata, kty: z.literal("EC"), alg: z.literal("ES256"), crv: z.literal("P-256"), x: z.string(), y: z.string() }).strict(),
    z.object({ ...signingKeyMetadata, kty: z.literal("RSA"), alg: z.enum(["RS256", "RS384", "RS512", "PS256", "PS384", "PS512"]), n: z.string(), e: z.string() }).strict(),
  ])).min(1).max(8),
}).strict();

export const workloadIssuerSchema = z.object({
  id: z.string(),
  tenant_id: z.string(),
  issuer: z.string(),
  jwks_uri: z.string().optional().default(""),
  trust_mode: issuerTrustModeSchema.optional().default("remote_jwks"),
  pinned_jwks: pinnedJWKSSchema.optional(),
  key_fingerprints: z.array(z.string()).optional(),
  allowed_audiences: z.array(z.string()),
  subject_claim: z.string(),
  // Narrow to the known bands; an unexpected future band falls back to the
  // conservative default for display rather than breaking the whole list parse.
  assurance_class: assuranceClassSchema.catch("cloud_instance"),
  // Absent when unclassified. `.catch(undefined)` so a category added to the
  // backend vocabulary ahead of this client degrades to unclassified for display
  // rather than failing the whole list parse — the roster staying readable matters
  // more than rendering a category this build has no label for.
  category: issuerCategorySchema.optional().catch(undefined),
  status: issuerStatusSchema,
  created_at: z.string(),
  updated_at: z.string(),
});
export type WorkloadIssuer = z.infer<typeof workloadIssuerSchema>;

export const workloadIssuersResponseSchema = z.object({
  data: z.array(workloadIssuerSchema),
});
export type WorkloadIssuersResponse = z.infer<typeof workloadIssuersResponseSchema>;

// Register a new trusted issuer. Issuer + at least one audience are required;
// the rest carry safe backend defaults (subject_claim defaults to "sub";
// assurance_class to cloud_instance). Each request chooses exactly one signing-key
// source. The server validates the key material and remains authoritative.
const issuerRegistrationFields = {
    issuer: z
      .string()
      .min(1, "Issuer URL is required")
      .url("Must be a valid HTTPS or spiffe:// issuer URL"),
    allowed_audiences: z
      .array(z.string().min(1))
      .min(1, "At least one audience is required"),
    subject_claim: z.string().optional(),
    // The census classification, confirmed by the operator at create time. It is
    // PRE-SELECTED from the provider preset or a well-known issuer URL (see
    // suggestIssuerCategory) and is freely changeable before submit — the
    // suggestion is a starting point a human accepts, never something the server
    // infers behind them (docs/product-areas.md).
    //
    // Optional, and leaving it unset is a legitimate answer: an operator who does
    // not yet know what an issuer is should record "unclassified" rather than
    // guessing, because a wrong category is indistinguishable from a right one on
    // the chart it feeds.
    category: issuerCategorySchema.optional(),
    // assurance_class is intentionally NOT part of the create input: the dialog
    // never sends it (the backend defaults it to cloud_instance, and the stronger
    // tiers 422 today), so exposing it here would invite a UI that's known to break.
};
export const registerIssuerSchema = z.discriminatedUnion("trust_mode", [
  z.object({
    ...issuerRegistrationFields,
    trust_mode: z.literal("remote_jwks"),
    jwks_uri: z.string().url("Provide the HTTPS signing-key URL"),
  }),
  z.object({
    ...issuerRegistrationFields,
    trust_mode: z.literal("pinned_jwks"),
    pinned_jwks: pinnedJWKSSchema,
  }),
]);
export type RegisterIssuerInput = z.infer<typeof registerIssuerSchema>;

// Provider presets are a UI-only affordance: picking one prefills the issuer URL
// hint + the typical audience so a non-specialist recognizes their setup. They
// name WHERE agents run (cloud workload identity / SPIRE) — never a vertical — so
// they stay within the generic product. "other" imposes nothing.
export type ProviderPreset = "eks" | "gke" | "aks" | "spire" | "other";
export interface ProviderPresetMeta {
  value: ProviderPreset;
  label: string;
  issuerPlaceholder: string;
  /** Seeded as a removable audience chip; "" means none (operator sets it). */
  defaultAudience: string;
  /** SPIFFE has no OIDC discovery → JWKS URI becomes required. */
  requiresJwks: boolean;
  /**
   * The census category this preset implies, pre-selected in the form for the
   * operator to confirm or change. Undefined for "other", which implies nothing.
   */
  defaultCategory?: IssuerCategory;
}
export const PROVIDER_PRESETS: ProviderPresetMeta[] = [
  {
    value: "eks",
    label: "Amazon EKS",
    issuerPlaceholder: "https://oidc.eks.<region>.amazonaws.com/id/EXAMPLE",
    defaultAudience: "sts.amazonaws.com",
    requiresJwks: false,
    defaultCategory: "cloud_workload",
  },
  {
    value: "gke",
    label: "Google GKE",
    issuerPlaceholder:
      "https://container.googleapis.com/v1/projects/<project>/locations/<loc>/clusters/<cluster>",
    defaultAudience: "",
    requiresJwks: false,
    defaultCategory: "cloud_workload",
  },
  {
    value: "aks",
    label: "Azure AKS",
    issuerPlaceholder: "https://<region>.oic.prod-aks.azure.com/<tenant>/<guid>/",
    defaultAudience: "api://AzureADTokenExchange",
    requiresJwks: false,
    defaultCategory: "cloud_workload",
  },
  {
    value: "spire",
    label: "SPIRE / SPIFFE",
    issuerPlaceholder: "spiffe://<trust-domain>",
    defaultAudience: "",
    requiresJwks: true,
    defaultCategory: "service_account",
  },
  {
    value: "other",
    label: "Other / generic OIDC",
    issuerPlaceholder: "https://issuer.example.com",
    defaultAudience: "",
    requiresJwks: false,
  },
];

/**
 * Well-known issuer hosts and the category they imply.
 *
 * A SUGGESTION ONLY. It pre-selects a value in the form that the operator sees and
 * can change before submitting; nothing here is ever written without a human
 * confirming it. That line is the whole reason this map lives in the console rather
 * than in the service: docs/product-areas.md draws it at exactly this point —
 * a value may be CHOSEN when a row is created and a person can see it, and may not
 * be substituted behind a reader's back. The migration that added the column
 * deliberately backfilled nothing for the same reason.
 *
 * Matched on host suffix rather than a full URL, because every one of these carries
 * a per-customer path or subdomain (an EKS cluster id, a GitLab group, an Azure
 * tenant guid). Order matters only in that the first match wins; the patterns are
 * disjoint today.
 *
 * An unmatched issuer suggests NOTHING. Most enterprise issuers are private —
 * internal SPIRE domains, self-hosted GitLab, a company OIDC endpoint — and
 * guessing at those is how a wrong classification would enter unnoticed. That case
 * is exactly what the operator-facing select is for.
 */
const WELL_KNOWN_ISSUER_CATEGORIES: ReadonlyArray<{
  match: (issuer: string) => boolean;
  category: IssuerCategory;
}> = [
  // CI/CD — a fresh identity per run.
  { match: (i) => i.includes("token.actions.githubusercontent.com"), category: "pipeline" },
  { match: (i) => i.includes("gitlab.com"), category: "pipeline" },
  { match: (i) => i.includes("agent.buildkite.com"), category: "pipeline" },
  { match: (i) => i.includes("oidc.circleci.com"), category: "pipeline" },
  { match: (i) => i.includes("app.terraform.io"), category: "pipeline" },
  // Cloud compute planes.
  { match: (i) => /oidc\.eks\.[^.]+\.amazonaws\.com/.test(i), category: "cloud_workload" },
  { match: (i) => i.includes("container.googleapis.com"), category: "cloud_workload" },
  { match: (i) => i.includes("oic.prod-aks.azure.com"), category: "cloud_workload" },
  { match: (i) => i.includes("login.microsoftonline.com"), category: "cloud_workload" },
  { match: (i) => i.includes("oidc.prod-aks.azure.com"), category: "cloud_workload" },
  // A SPIFFE trust domain is a named-service issuer by construction.
  { match: (i) => i.startsWith("spiffe://"), category: "service_account" },
];

/**
 * suggestIssuerCategory proposes a category for an issuer URL, falling back to the
 * selected provider preset. Returns undefined when nothing is recognized — which
 * leaves the form on "Unclassified", the truthful answer.
 *
 * The issuer URL wins over the preset: the preset is what the operator said they
 * were setting up, the URL is what they actually pasted, and when those disagree
 * the URL is the fact.
 */
export function suggestIssuerCategory(
  issuer: string,
  presetCategory?: IssuerCategory,
): IssuerCategory | undefined {
  const trimmed = issuer.trim().toLowerCase();
  if (trimmed !== "") {
    const hit = WELL_KNOWN_ISSUER_CATEGORIES.find((c) => c.match(trimmed));
    if (hit) return hit.category;
  }
  return presetCategory;
}
